import { useMutation, useQuery, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";

export interface Locale {
    id: number;
    code: string | null;
    name: string | null;
    isDefault: boolean;
    isEnabled: boolean;
    flag: string | null;
    nativeName: string | null;
    direction: string;
}

export interface LocaleUpdateRequest {
    locales: Array<{
        code: string;
        name: string;
        nativeName?: string;
        flag?: string;
        direction: string;
    }>;
    defaultLocale: string;
}

export interface LocaleUpdateResponse {
    enabledLocales: Locale[];
    defaultLocale: string;
    message: string;
}

// Fetch all locales
export const useGetLocales = () => {
    return useQuery({
        queryKey: ["locales"],
        queryFn: async (): Promise<Locale[]> => {
            const response = await loggedInApiCall.get("/api/locales");
            return response.data["hydra:member"];
        },
    });
};

// Fetch enabled locales only
export const useGetEnabledLocales = () => {
    return useQuery({
        queryKey: ["locales", "enabled"],
        queryFn: async (): Promise<Locale[]> => {
            const response = await loggedInApiCall.get("/api/locales");
            const allLocales = response.data["hydra:member"];
            return allLocales.filter((locale: Locale) => locale.isEnabled);
        },
    });
};

// Get default locale
export const useGetDefaultLocale = () => {
    return useQuery({
        queryKey: ["locales", "default"],
        queryFn: async (): Promise<Locale | null> => {
            const response = await loggedInApiCall.get("/api/locales");
            const allLocales = response.data["hydra:member"];
            const defaultLocale = allLocales.find((locale: Locale) => locale.isDefault);
            return defaultLocale || null;
        },
    });
};

// Update locales configuration
export const useUpdateLocales = () => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (data: LocaleUpdateRequest): Promise<LocaleUpdateResponse> => {
            const response = await loggedInApiCall.post("/api/locales/bulk-update", data);
            return response.data;
        },
        onSuccess: () => {
            // Invalidate related queries
            queryClient.invalidateQueries(["locales"]);
        },
    });

    return {
        mutateAsync: mutation.mutateAsync,
        isPending: mutation.isLoading,
        error: mutation.error,
        isSuccess: mutation.isSuccess,
    };
};
